import api from "../axios";

/**
 * Competency API - HSE/KS Competency Management
 * ISO 9001:2015 Clause 7.2 - Competence
 */

// ==================== Competency Definitions ====================

/**
 * Get all competency definitions with optional filters
 * @param {Object} filters - Filter options
 * @param {string} filters.search - Search query
 * @param {string} filters.category - Filter by category
 * @param {string} filters.type - Filter by type (cv, certificate, course, etc.)
 * @param {boolean} filters.is_active - Filter by active status
 */
export const getCompetencyDefinitions = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("search", filters.search);
  if (filters.category) params.append("category", filters.category);
  if (filters.type) params.append("type", filters.type);
  if (filters.is_active !== undefined) params.append("is_active", filters.is_active);
  
  const response = await api.get(`/hse_ks/competency_definitions?${params.toString()}`);
  return response.data;
};

/**
 * Get a specific competency definition by ID
 * @param {number} id - Competency definition ID
 */
export const getCompetencyDefinition = async (id) => {
  const response = await api.get(`/hse_ks/competency_definitions/${id}`);
  return response.data;
};

/**
 * Create a new competency definition
 * @param {Object} definitionData - Competency definition data
 */
export const createCompetencyDefinition = async (definitionData) => {
  const payload = { competency_definition: definitionData };
  const response = await api.post("/hse_ks/competency_definitions", payload);
  return response.data;
};

/**
 * Update an existing competency definition
 * @param {number} id - Competency definition ID
 * @param {Object} definitionData - Updated definition data
 */
export const updateCompetencyDefinition = async (id, definitionData) => {
  const payload = { competency_definition: definitionData };
  const response = await api.patch(`/hse_ks/competency_definitions/${id}`, payload);
  return response.data;
};

/**
 * Delete a competency definition
 * @param {number} id - Competency definition ID
 */
export const deleteCompetencyDefinition = async (id) => {
  const response = await api.delete(`/hse_ks/competency_definitions/${id}`);
  return response.data;
};

// ==================== Employee Competencies ====================

/**
 * Get current user's competencies
 * @param {Object} filters - Filter options
 */
export const getMyCompetencies = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("search", filters.search);
  if (filters.status) params.append("status", filters.status);
  if (filters.type) params.append("type", filters.type);
  
  const response = await api.get(`/hse_ks/competencies/my?${params.toString()}`);
  return response.data;
};

/**
 * Get all employees' competencies (admin/manager only)
 * @param {Object} filters - Filter options
 */
export const getEmployeesCompetencies = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("search", filters.search);
  if (filters.department) params.append("department", filters.department);
  if (filters.status) params.append("status", filters.status);
  
  const response = await api.get(`/hse_ks/competencies/employees?${params.toString()}`);
  return response.data;
};

/**
 * Get a specific employee's competencies
 * @param {number} employeeId - Employee user ID
 * @param {Object} filters - Filter options
 */
export const getEmployeeCompetencies = async (employeeId, filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.status) params.append("status", filters.status);
  if (filters.type) params.append("type", filters.type);
  
  const response = await api.get(`/hse_ks/competencies/employees/${employeeId}?${params.toString()}`);
  return response.data;
};

/**
 * Assign a competency to an employee
 * @param {number} employeeId - Employee user ID
 * @param {Object} competencyData - Competency assignment data
 */
export const assignCompetencyToEmployee = async (employeeId, competencyData) => {
  const payload = { competency: competencyData };
  const response = await api.post(`/hse_ks/competencies/employees/${employeeId}/competencies`, payload);
  return response.data;
};

/**
 * Update an employee's competency
 * @param {number} employeeId - Employee user ID
 * @param {number} competencyId - Competency ID
 * @param {Object} competencyData - Updated competency data
 */
export const updateEmployeeCompetency = async (employeeId, competencyId, competencyData) => {
  const payload = { competency: competencyData };
  const response = await api.patch(`/hse_ks/competencies/employees/${employeeId}/competencies/${competencyId}`, payload);
  return response.data;
};

/**
 * Remove a competency from an employee
 * @param {number} employeeId - Employee user ID
 * @param {number} competencyId - Competency ID
 */
export const removeEmployeeCompetency = async (employeeId, competencyId) => {
  const response = await api.delete(`/hse_ks/competencies/employees/${employeeId}/competencies/${competencyId}`);
  return response.data;
};

// ==================== Competency Matrix ====================

/**
 * Get competency matrix (role-based requirements)
 * @param {Object} filters - Filter options
 */
export const getCompetencyMatrix = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("search", filters.search);
  if (filters.department) params.append("department", filters.department);
  
  const response = await api.get(`/hse_ks/competencies/matrix?${params.toString()}`);
  return response.data;
};

/**
 * Get competency matrix for a specific role
 * @param {number} roleId - Role ID
 */
export const getRoleCompetencyMatrix = async (roleId) => {
  const response = await api.get(`/hse_ks/competencies/matrix/roles/${roleId}`);
  return response.data;
};

/**
 * Update role competency requirements
 * @param {number} roleId - Role ID
 * @param {Object} requirements - Required competencies
 */
export const updateRoleCompetencyRequirements = async (roleId, requirements) => {
  const payload = { requirements };
  const response = await api.patch(`/hse_ks/competencies/matrix/roles/${roleId}`, payload);
  return response.data;
};

// ==================== Roles ====================

/**
 * Get all roles
 * @param {Object} filters - Filter options
 */
export const getRoles = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("search", filters.search);
  if (filters.department) params.append("department", filters.department);
  
  const response = await api.get(`/hse_ks/roles?${params.toString()}`);
  return response.data;
};

/**
 * Get a specific role by ID
 * @param {number} id - Role ID
 */
export const getRole = async (id) => {
  const response = await api.get(`/hse_ks/roles/${id}`);
  return response.data;
};

/**
 * Create a new role
 * @param {Object} roleData - Role data
 */
export const createRole = async (roleData) => {
  const payload = { role: roleData };
  const response = await api.post("/hse_ks/roles", payload);
  return response.data;
};

/**
 * Update an existing role
 * @param {number} id - Role ID
 * @param {Object} roleData - Updated role data
 */
export const updateRole = async (id, roleData) => {
  const payload = { role: roleData };
  const response = await api.patch(`/hse_ks/roles/${id}`, payload);
  return response.data;
};

/**
 * Delete a role
 * @param {number} id - Role ID
 */
export const deleteRole = async (id) => {
  const response = await api.delete(`/hse_ks/roles/${id}`);
  return response.data;
};

// ==================== Courses ====================

/**
 * Get all courses
 * @param {Object} filters - Filter options
 */
export const getCourses = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.search) params.append("search", filters.search);
  if (filters.status) params.append("status", filters.status);
  if (filters.competency_definition_id) params.append("competency_definition_id", filters.competency_definition_id);
  
  const response = await api.get(`/hse_ks/courses?${params.toString()}`);
  return response.data;
};

/**
 * Get a specific course by ID
 * @param {number} id - Course ID
 */
export const getCourse = async (id) => {
  const response = await api.get(`/hse_ks/courses/${id}`);
  return response.data;
};

/**
 * Create a new course
 * @param {Object} courseData - Course data
 */
export const createCourse = async (courseData) => {
  const payload = { course: courseData };
  const response = await api.post("/hse_ks/courses", payload);
  return response.data;
};

/**
 * Update an existing course
 * @param {number} id - Course ID
 * @param {Object} courseData - Updated course data
 */
export const updateCourse = async (id, courseData) => {
  const payload = { course: courseData };
  const response = await api.patch(`/hse_ks/courses/${id}`, payload);
  return response.data;
};

/**
 * Delete a course
 * @param {number} id - Course ID
 */
export const deleteCourse = async (id) => {
  const response = await api.delete(`/hse_ks/courses/${id}`);
  return response.data;
};

/**
 * Enroll employees in a course
 * @param {number} courseId - Course ID
 * @param {Array<number>} employeeIds - Array of employee user IDs
 */
export const enrollEmployeesInCourse = async (courseId, employeeIds) => {
  const payload = { employee_ids: employeeIds };
  const response = await api.post(`/hse_ks/courses/${courseId}/enrollments`, payload);
  return response.data;
};

/**
 * Complete a course enrollment
 * @param {number} courseId - Course ID
 * @param {number} enrollmentId - Enrollment ID
 * @param {Object} completionData - Completion data (grade, certificate, etc.)
 */
export const completeCourseEnrollment = async (courseId, enrollmentId, completionData) => {
  const payload = { enrollment: completionData };
  const response = await api.patch(`/hse_ks/courses/${courseId}/enrollments/${enrollmentId}/complete`, payload);
  return response.data;
};

// ==================== Expiring Competencies ====================

/**
 * Get expiring competencies
 * @param {Object} filters - Filter options
 * @param {number} filters.days - Number of days ahead to check (default: 30)
 */
export const getExpiringCompetencies = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.days) params.append("days", filters.days);
  if (filters.employee_id) params.append("employee_id", filters.employee_id);
  
  const response = await api.get(`/hse_ks/competencies/expiring?${params.toString()}`);
  return response.data;
};

// ==================== Training Plan ====================

/**
 * Get training plan
 * @param {Object} filters - Filter options
 */
export const getTrainingPlan = async (filters = {}) => {
  const params = new URLSearchParams();
  
  if (filters.employee_id) params.append("employee_id", filters.employee_id);
  if (filters.year) params.append("year", filters.year);
  
  const response = await api.get(`/hse_ks/training_plans?${params.toString()}`);
  return response.data;
};

/**
 * Get a specific training plan item by ID
 * @param {number} id - Training plan item ID
 */
export const getTrainingPlanItem = async (id) => {
  const response = await api.get(`/hse_ks/training_plans/${id}`);
  return response.data;
};

/**
 * Create or update training plan item
 * @param {Object} planData - Training plan data
 */
export const upsertTrainingPlanItem = async (planData) => {
  const payload = { training_plan_item: planData };
  if (planData.id) {
    const response = await api.patch(`/hse_ks/training_plans/${planData.id}`, payload);
    return response.data;
  } else {
    const response = await api.post("/hse_ks/training_plans", payload);
    return response.data;
  }
};

